import { useEffect } from "react";
import { careers } from "../../../datasets/careers";
import { useNonce } from "../../../../context/NonceContext";

/* global whr_embed */

export function WorkableJobWidget() {
  const { nonce } = useNonce();
  useEffect(() => {
    /**
     * Function checks if script is loaded and evaluated. If it is, ommits adding script tag and goes straight to loading offers.
     * If not, adds it first and then load offers.
     *
     * Rationale: Script tag may already have been added during Server Side Rendering. To prevent having two identical tags
     * on the page, and consequently executing the script twice, producing a risk of a random failure due to race condition,
     * this check must be performed.
     */

    function onLoad() {
      // If onLoad is invoked before the script is evaluated, throw error
      if (!("whr_embed" in window)) {
        console.error("Loading whr_embed failed.");
        return;
      }

      // If whr_embed global variable is present, load offers
      whr_embed(54467, {
        detail: "titles",
        base: "jobs",
        zoom: "city",
        grouping: "none",
      });
    }

    // If script is already loaded and evaluated, invoke onLoad
    if ("whr_embed" in window) {
      onLoad();
      return;
    }

    // If script is not yet loaded and evaluated, check if it's present (i.e. loading) or not (then create script tag) and attach onLoad to it.
    const url = "https://www.workable.com/assets/embed.js";
    const script =
      document.querySelector(`script[src="${url}"]`) ||
      (() => {
        const scriptTag = document.createElement("script");
        scriptTag.async = true;
        scriptTag.src = url;
        !!nonce && scriptTag.setAttribute("nonce", nonce);
        document.body.appendChild(scriptTag);
        return scriptTag;
      })();

    script.onload = onLoad;
  }, [nonce]);

  return (
    <div className="whr-container">
      <div className="whr-row">
        <div id="whr_embed_hook" />
        <br />
        <br />
      </div>
    </div>
  );
}

export const CareersOffers = () => {
  return (
    <section className="careers_offers-background">
      <section className="careers_offers-main">
        <h1 className="careers_offers-header">{careers.header}</h1>
        <div className="careers_offers-main-text">
          <p className="careers_offers-paragraph">
            Modern Enterprises are now adopting data as a first class citizen, with a mentality that
            external data has the breadth and richness that is critical to every function of an
            organization. Yet, getting your external data house in order at scale is proving to be
            much harder than Enterprises expected. Demyst is an External Data Management Platform,
            providing data teams with the toolkit required to manage all their external data. We
            empower data teams to enable their business users by orchestrating at scale, tailored
            pipelines that provide trusted external data. With Demyst, Data teams experience more
            data and more trust with less effort, improving data ROI and driving outcomes through
            their business users. Don't miss out on the opportunity to play a vital role in
            revolutionizing data management for enterprises.
          </p>
        </div>
        <h1 className="careers_offers-summary">{careers.heading}</h1>
        <WorkableJobWidget />
        <div className="careers_offers-main-text">
          <p className="careers_offers-paragraph">
            Demyst is a wonderful place to work, but being a coveted workplace means we are
            vulnerable to recruitment phishing scams. Most of these have individuals posing as
            representatives of prominent companies, including Demyst, with the aim of obtaining
            personal, sensitive, or financial information or extracting payment from would-be
            candidates. The scams prey upon an individual’s desire to obtain a job and can sometimes
            feel like a genuine recruitment process.
          </p>
        </div>
        <div className="careers_offers-main-text">
          <p></p>
          <p className="careers_offers-paragraph">
            We urge all job seekers and candidates to be aware of such potential scams.
          </p>
          <p className="careers_offers-paragraph">
            Should you encounter a recruitment process that claims to be for Demyst but is not
            consistent with the legitimate recruitment process, please do not provide any personal
            or financial information:
          </p>
          <ul>
            <li>
              Most importantly, Demyst employees and recruiters working on our behalf will never
              solicit personal data (e.g., date of birth, social security numbers, or bank account
              information) from candidates during the application and interview phase. An
              out-of-the-blue request for such data is the biggest flag that the interaction is not
              legitimate.
            </li>
            <li>
              Demyst may request some personal information to start the hiring process or draft a
              contract, but this will take place after a full recruitment process which includes
              multiple interviews by Demyst employees, including the hiring manager.
            </li>
            <li>
              Demyst’s recruitment processes include communication with candidates through
              recognized professional networks, such as LinkedIn. However, further communication is
              always through an official Demyst email address (from the @demystdata.com domain),
              over the phone or through recruitment platforms such as Workable. While we sometimes
              work with external recruiters which may have non-Demyst email addresses, they will
              connect you Demyst employees with official Demyst email addresses for interviews.
            </li>
            <li>
              Demyst’s interviews are conducted over the phone or via Zoom with video activated.
            </li>
          </ul>
          <p className="careers_offers-paragraph">
            If you think that you have been a victim of fraud:
          </p>

          <ul>
            <li>Check the recruiter’s identity on LinkedIn</li>
            <li>
              Check the existence of the position on our website{" "}
              <a href="https://demyst.com/careers">https://demyst.com/careers</a>
            </li>
            <li>
              Notify our recruitment team at{" "}
              <a href="mailto:hiring@demystdata.com">hiring@demystdata.com</a>
            </li>
            <li>
              Contact your local authorities authorities, such as
              <ul>
                <li>
                  <a href="https://www.ftccomplaintassistant.gov/#crnt&panel1-1">
                    https://www.ftccomplaintassistant.gov/#crnt&panel1-1
                  </a>{" "}
                  in the US
                </li>
                <li>
                  <a href="https://www.scamwatch.gov.au/report-a-scam">
                    https://www.scamwatch.gov.au/report-a-scam
                  </a>{" "}
                  in Australia, or
                </li>
                <li>
                  <a href="https://www.police.gov.sg/iwitness">
                    https://www.police.gov.sg/iwitness
                  </a>{" "}
                  in the US
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </section>
    </section>
  );
};
